import qs from 'qs';

import { client, CustomAxiosRequestConfig } from '@api/client';

import { ISchemaConfigUploadUrl } from '@interfaces/cmModule/schemaConfig';
import { TEmptyObject } from '@interfaces/common/common';
import { IItemsResponse } from '@interfaces/common/response';
import {
	IGetOrganisationsDTO,
	IOrganisationFormDTO,
	IOrganizationsCheckUniqueValuesDTO,
	IUploadLogoURLDTO,
} from '@interfaces/organisation/dto/organisationsDTO';
import { IOrganisationEntity, IOrganisationProfile } from '@interfaces/organisation/organisations';
import { IRolesResponse, IUserEntity } from '@interfaces/user/user';

import { URL } from './urls';

const {
	block,
	deleteOrganisations,
	organizationProfile,
	organizationsAll,
	organizationsActive,
	organizationsAssetOwnersFilter,
	organizationsCheckValues,
	organizationsCreate,
	organizationsEdit,
	organizationsFilter,
	organizationCurrentUsers,
	unblock,
	createUploadLogoUrl,
} = URL;

export const getOrganisations = ({
	page,
	pageSize,
	sortDirection,
	sortFieldName,
	searchValue,
	isActive,
	organisationType,
}: IGetOrganisationsDTO): Promise<IItemsResponse<IUserEntity>> => client.get(`/${organizationsFilter}`, {
	params: {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		isActive,
		organisationType,
	},
});

export const createOrganisation = (data: IOrganisationFormDTO): Promise<void> => {
	return client.post(`/${organizationsCreate}`, data);
};

export const getOrganisationProfile = (id?: string): Promise<IOrganisationProfile | TEmptyObject> => {
	return client.get(`/${organizationProfile}/${id}`);
};

export const editOrganisationProfile = ({ id, values }: {
	id?: string;
	values?: IOrganisationFormDTO;
}): Promise<void> => client.put(`/${organizationsEdit}/${id}`, {
	...values,
});

export const organizationsCheckUniqueValues = ({
	name,
	legalName,
	email,
	organizationId,
}: IOrganizationsCheckUniqueValuesDTO): Promise<void> =>
	client.post(`/${organizationsCheckValues}`, {
		name,
		legalName,
		email,
		organizationId,
	});

export const removeOrganisations = (ids: Array<string>): Promise<void> =>
	client.delete(`/${deleteOrganisations}`, {
		params: {
			ids,
		},
		paramsSerializer: params => qs.stringify(params, { indices: false }),
	});

export const removeOrganisation = (id: string): Promise<void> => client.delete(`/${deleteOrganisations}/${id}`);

export const blockOrganisation = (id: string): Promise<void> =>
	client.post(`/${organizationProfile}/${id}/${block}`, {});

export const unblockOrganisation = (id: string): Promise<void> => {
	return client.post(`/${organizationProfile}/${id}/${unblock}`, {});
};

export const getAllOrganizations = (): Promise<IOrganisationEntity[]> => {
	return client.get(`/${organizationsAll}`);
};

export const getActiveOrganizations = (): Promise<IOrganisationEntity[]> => {
	return client.get(`/${organizationsActive}`);
};

export const getAssetOwnersForFilter = (): Promise<IOrganisationEntity[]> => {
	return client.get(`/${organizationsAssetOwnersFilter}`);
};

export const getOrganisationCurrentUsers = ({ organizationId }: { organizationId: string }): Promise<IUserEntity[]> => {
	return client.get(`/${organizationCurrentUsers}`, {
		params: { organizationId },
		skipOrgHeader: true,
	} as CustomAxiosRequestConfig);
};

export const getUploadLogoURL = ({
	fileName,
	url = createUploadLogoUrl,
}: IUploadLogoURLDTO): Promise<ISchemaConfigUploadUrl> => {
	return client.post(url, { fileName });
};

export const getOrganizationById = (id: string): Promise<IOrganisationEntity> => {
	return client.get(`organizations/${id}`, {
		params: { id },
	});
};

export const getOrganizationsByIds = (ids: string[]): Promise<IOrganisationEntity[]> => {
	return Promise.all(ids.map(id => getOrganizationById(id)));
};

export const getRevokedRoles = (organisationId: string): Promise<IRolesResponse[]> => {
	return client.get(`/organizations/${organisationId}/saved-role-availability`);
};
